import { b2x } from '@b2x/react/src';
import React from 'react';

import { AccountOffcanvasProps, useAccountOffcanvas } from './AccountOffcanvas';
import { CartOffcanvasProps, useCartOffcanvas } from './CartOffcanvas';

interface AppContextInterface {
  AccountOffcanvas: React.FunctionComponentElement<AccountOffcanvasProps>;
  CartOffcanvas: React.FunctionComponentElement<CartOffcanvasProps>;
  footerCopyrightRef: React.RefObject<HTMLDivElement>;
  headerCheckout: boolean;
  headerFirstRowRef: React.RefObject<HTMLDivElement>;
  headerHeight?: number;
  isFooterCopyrightVisible: boolean;
  isHeaderFirstRowVisible: boolean;
  thingsToLoadBeforeAppReady: Record<string, unknown>;
  topBarDesktopHeight?: number;
  topBarMobileHeight?: number;
}

export const [AppContextProvider, useAppContext] = b2x.createContext<AppContextInterface>('AppContext');

interface AppStaticContextInterface {
  setFooterCopyrightRef: React.RefCallback<HTMLDivElement>;
  setHeaderCheckout: React.Dispatch<React.SetStateAction<boolean>>;
  setHeaderFirstRowRef: React.RefCallback<HTMLDivElement>;
  setHeaderHeight: React.Dispatch<React.SetStateAction<number | undefined>>;
  setThingsToLoadBeforeAppReady: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
  setTopBarDesktopHeight: React.Dispatch<React.SetStateAction<number | undefined>>;
  setTopBarMobileHeight: React.Dispatch<React.SetStateAction<number | undefined>>;
  showAccountOffcanvas(): void;
  showCartOffcanvas(): void;
}

export const [AppStaticContextProvider, useAppStaticContext] =
  b2x.createContext<AppStaticContextInterface>('AppStaticContext');

interface UseAppContextInitializerProps {}

const useAppContextInitializer = (props: UseAppContextInitializerProps) => {
  const [AccountOffcanvas, showAccountOffcanvas] = useAccountOffcanvas();
  const [CartOffcanvas, showCartOffcanvas] = useCartOffcanvas();
  const [headerHeight, setHeaderHeight] = React.useState<number>();
  const [headerCheckout, setHeaderCheckout] = React.useState<boolean>(false);
  const [thingsToLoadBeforeAppReady, setThingsToLoadBeforeAppReady] = React.useState<Record<string, unknown>>({});
  const [headerFirstRowRef, setHeaderFirstRowRef, isHeaderFirstRowVisible] =
    b2x.useIntersectionObserver<HTMLDivElement>(true);
  const [footerCopyrightRef, setFooterCopyrightRef, isFooterCopyrightVisible] =
    b2x.useIntersectionObserver<HTMLDivElement>(true);
  const [topBarDesktopHeight, setTopBarDesktopHeight] = React.useState<number>();
  const [topBarMobileHeight, setTopBarMobileHeight] = React.useState<number>();

  const appContext: AppContextInterface = React.useMemo(
    () => ({
      AccountOffcanvas,
      CartOffcanvas,
      footerCopyrightRef,
      headerCheckout,
      headerFirstRowRef,
      headerHeight,
      isFooterCopyrightVisible,
      isHeaderFirstRowVisible,
      thingsToLoadBeforeAppReady,
      topBarDesktopHeight,
      topBarMobileHeight,
    }),
    [
      AccountOffcanvas,
      CartOffcanvas,
      footerCopyrightRef,
      headerCheckout,
      headerFirstRowRef,
      headerHeight,
      isFooterCopyrightVisible,
      isHeaderFirstRowVisible,
      thingsToLoadBeforeAppReady,
      topBarDesktopHeight,
      topBarMobileHeight,
    ]
  );

  const appStaticContext: AppStaticContextInterface = React.useMemo(
    () => ({
      setFooterCopyrightRef,
      setHeaderCheckout,
      setHeaderFirstRowRef,
      setHeaderHeight,
      setThingsToLoadBeforeAppReady,
      setTopBarDesktopHeight,
      setTopBarMobileHeight,
      showAccountOffcanvas,
      showCartOffcanvas,
    }),
    [setFooterCopyrightRef, setHeaderFirstRowRef, showAccountOffcanvas, showCartOffcanvas]
  );

  return {
    AppContextProvider,
    AppStaticContextProvider,
    appContext,
    appStaticContext,
  };
};

export interface AppContextProps extends UseAppContextInitializerProps {
  children:
    | React.ReactNode
    | ((appContext: AppContextInterface, appStaticContext: AppStaticContextInterface) => React.ReactNode);
}

export const AppContext = ({ children, ...otherProps }: AppContextProps) => {
  const appContextInitializer = useAppContextInitializer(otherProps);
  return (
    <appContextInitializer.AppContextProvider value={appContextInitializer.appContext}>
      <appContextInitializer.AppStaticContextProvider value={appContextInitializer.appStaticContext}>
        {typeof children === 'function'
          ? children(appContextInitializer.appContext, appContextInitializer.appStaticContext)
          : children}
      </appContextInitializer.AppStaticContextProvider>
    </appContextInitializer.AppContextProvider>
  );
};
